import { useEffect, useState } from 'react'

import { FormControlLabel, Radio, RadioGroup, Typography } from '@material-ui/core'
import { NonDismissibleDialog } from '@scoir/modals'
import { cloneDeep, isEqual } from 'lodash'
import { useDispatch } from 'react-redux'

import EditProfileForm from 'components/EditProfileForm'
import { validateLink } from 'components/LinkField'
import { useAppSelector } from 'store'
import { User, actions as userActions } from 'store/user'

import MeetingLinkInput from '../../routes/visits/Visits/View/CalendarEvent/VisitOpening/MeetingLinkInput'
import './RegisterModal.scss'

type RequestModalProps = {
  children: React.ReactNode
  show: boolean
  title: string
  onClose: () => void
  onConfirm: (placeName?: string, virtual?: boolean) => void
  allowRepMeeting?: boolean
  placeName?: string
  isConfirming?: boolean
  hasRegisterError?: boolean
  allowRepLocationType: boolean
  meetingLink?: string
}
const RegisterModal = ({
  children,
  show,
  title,
  onClose,
  onConfirm,
  allowRepMeeting,
  placeName,
  meetingLink,
  isConfirming = false,
  hasRegisterError = false,
  allowRepLocationType,
}: RequestModalProps) => {
  const user = useAppSelector((state) => state.user.record)
  const [isValid, setIsValid] = useState(true)
  const [isValidPlaceName, setIsValidPlaceName] = useState(true)
  const [updatedUserFields, setUpdatedUserFields] = useState(false)
  const [editUser, setEditUser] = useState(cloneDeep(user))
  const [errorSavingUser, setErrorSavingUser] = useState(false)
  const [repPlaceName, setRepPlaceName] = useState(allowRepLocationType ? meetingLink || '' : placeName || '')
  const dispatch = useDispatch()
  const [isVirtual, setIsVirtual] = useState<boolean>()
  const displayMeetingLink =
    (allowRepLocationType && isVirtual && allowRepMeeting) || (!allowRepLocationType && allowRepMeeting)

  useEffect(() => {
    if (!isEqual(user, editUser)) {
      setEditUser(editUser)
    }
  }, [editUser, user])

  const cancel = () => {
    onClose()
    setIsValid(true)
    setIsValidPlaceName(true)
    setUpdatedUserFields(false)
    setEditUser(cloneDeep(user))
    setErrorSavingUser(false)
  }
  const confirm = () => {
    if (updatedUserFields) {
      try {
        setErrorSavingUser(false)
        dispatch(userActions.updateUser(editUser))
      } catch {
        setErrorSavingUser(true)
        return
      }
    }

    onConfirm(repPlaceName, isVirtual)
  }
  const update = (editUser: User, isValid: boolean) => {
    setIsValid(isValid)
    setEditUser(editUser)
    setUpdatedUserFields(true)
  }
  const cancelEditing = () => {
    setEditUser(cloneDeep(user))
  }
  const updateMeetingPlaceName = (newPlacename = '') => {
    setIsValidPlaceName(validateLink(newPlacename))
    setRepPlaceName(newPlacename)
  }
  return (
    <NonDismissibleDialog
      open={show}
      headerText={title}
      className="register-modal-comp"
      onClose={() => cancel()}
    >
      <NonDismissibleDialog.Body>
        {children}
        <b className="section-title section-border">Review & update your profile info</b>
        <div className="profile-update-section">
          <EditProfileForm
            className="edit-profile-card"
            editUser={editUser}
            onCancel={() => cancelEditing()}
            onUpdate={(editUser: User, isValid: boolean) => update(editUser, isValid)}
          />
          {allowRepLocationType && (
            <>
              <b className="section-title">Choose Meeting Location*</b>
              <RadioGroup
                aria-label="meeting-location"
                name="meeting-location"
                value={isVirtual}
                onChange={(e) => setIsVirtual(e.target.value === 'virtual')}
              >
                <FormControlLabel
                  value="in-person"
                  control={<Radio color="primary" />}
                  label="In Person"
                />
                <FormControlLabel
                  value="virtual"
                  control={<Radio color="primary" />}
                  label="Virtual"
                />
              </RadioGroup>
            </>
          )}
          {displayMeetingLink && (
            <>
              <b className="section-title">Review or add virtual meeting link</b>
              <div className="meeting-link-container">
                <MeetingLinkInput
                  meetingLink={repPlaceName}
                  onChange={updateMeetingPlaceName}
                />
              </div>
            </>
          )}
        </div>
      </NonDismissibleDialog.Body>
      <NonDismissibleDialog.Footer>
        {errorSavingUser && (
          <Typography
            color="error"
            variant="body2"
          >
            An error occurred while attempting to save changes to your profile.
          </Typography>
        )}
        {hasRegisterError && (
          <Typography
            color="error"
            variant="body2"
          >
            An error occurred while attempting to register for this visit.
          </Typography>
        )}
        <NonDismissibleDialog.Footer.CloseButton>Cancel</NonDismissibleDialog.Footer.CloseButton>
        <NonDismissibleDialog.Footer.PrimaryActionButton
          disabled={!isValid || !isValidPlaceName || isConfirming || (allowRepLocationType && isVirtual === undefined)}
          onClick={() => confirm()}
        >
          Confirm
        </NonDismissibleDialog.Footer.PrimaryActionButton>
      </NonDismissibleDialog.Footer>
    </NonDismissibleDialog>
  )
}

export default RegisterModal
